import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  HomeIcon,
  UserGroupIcon,
  CubeIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ArrowUturnDownIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const roleBase = useMemo(() => {
    if (user?.role === 'agent') return '/agent';
    if (user?.role === 'depot_sales') return '/depot-sales';
    if (user?.role === 'finance') return '/finance';
    return '/admin';
  }, [user?.role]);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, roles: ['admin', 'finance', 'agent', 'depot_sales'] },
    { name: 'Products', path: '/products', icon: CubeIcon, roles: ['admin', 'finance'] },
    { name: 'Stock', path: '/stock', icon: ChartBarIcon, roles: ['admin', 'finance', 'agent', 'depot_sales'] },
    { name: 'Transactions', path: '/transactions', icon: CurrencyDollarIcon, roles: ['admin', 'finance', 'agent', 'depot_sales'] },
    { name: 'Debts', path: '/debts', icon: ExclamationTriangleIcon, roles: ['admin', 'finance', 'agent', 'depot_sales'] },
    { name: 'Balances', path: '/balances', icon: CurrencyDollarIcon, roles: ['admin'] },
    { name: 'Expenses', path: '/expenses', icon: DocumentTextIcon, roles: ['admin', 'finance'] },
    { name: 'Reports', path: '/reports', icon: ChartBarIcon, roles: ['admin', 'finance', 'agent', 'depot_sales'] },
    { name: 'Returns', path: '/returns', icon: ArrowUturnDownIcon, roles: ['admin', 'finance', 'agent', 'depot_sales'] },
    { name: 'Depot Returns', path: '/depot-returns', icon: ArrowUturnDownIcon, roles: ['agent', 'depot_sales'] },
    { name: 'Settlements', path: '/settlements', icon: DocumentTextIcon, roles: ['admin', 'finance'] },
    { name: 'Users', path: '/users', icon: UserGroupIcon, roles: ['admin'] },
  ];

  const filteredNavigation = navigation
    .filter(item => item.roles.includes(user?.role))
    .map(item => ({
      ...item,
      href: item.href || `${roleBase}${item.path}`
    }));

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-100 via-accent-50 to-primary-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gradient-to-br from-gray-900/70 to-primary-900/70 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-gradient-to-b from-primary-700 via-primary-600 to-primary-800 shadow-2xl">
          <div className="flex h-16 items-center justify-between px-4 border-b border-primary-500/30">
            <h1 className="text-2xl font-bold text-white">Royal Fresh</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-white/70 hover:text-white transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-2 px-3 py-4">
            {filteredNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-white text-primary-700 shadow-lg transform scale-105'
                    : 'text-white/90 hover:bg-primary-500 hover:text-white hover:shadow-md'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon
                  className={`mr-3 h-6 w-6 ${
                    isActive(item.href) ? 'text-primary-600' : 'text-white/80'
                  }`}
                />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-gradient-to-b from-primary-700 via-primary-600 to-primary-800 shadow-xl">
          <div className="flex h-16 items-center px-4 border-b border-primary-500/30">
            <h1 className="text-2xl font-bold text-white">Royal Fresh</h1>
          </div>
          <nav className="flex-1 space-y-2 px-3 py-4">
            {filteredNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-white text-primary-700 shadow-lg transform scale-105'
                    : 'text-white/90 hover:bg-primary-500 hover:text-white hover:shadow-md'
                }`}
              >
                <item.icon
                  className={`mr-3 h-6 w-6 ${
                    isActive(item.href) ? 'text-primary-600' : 'text-white/80'
                  }`}
                />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b-2 border-primary-200 bg-gradient-to-r from-white to-secondary-50 px-4 shadow-md sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-primary-700 hover:text-primary-900 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          {location.pathname !== '/dashboard' && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-x-2 rounded-lg px-3 py-2 text-sm font-semibold text-primary-700 hover:text-white hover:bg-primary-600 border border-primary-300 hover:border-primary-600 transition-all duration-200 shadow-sm"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back
            </button>
          )}

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="flex items-center gap-x-2 bg-primary-50 px-4 py-2 rounded-lg border border-primary-200">
                <UserIcon className="h-5 w-5 text-primary-600" />
                <span className="text-sm font-semibold text-primary-900">
                  {user?.name} <span className="text-primary-600">({user?.role})</span>
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-x-2 text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
